import _styled from '@emotion/styled/base';
import { css } from '@emotion/react';
import { size, text, border } from '@hubble/tokens/web';
import { getFontSize, getSize, FORM_ELEMENT_TRANSITION_VALUE } from '../../forms/shared/utils.js';
import { Spacing } from '../../primitives/spacing.js';

const FORM_ELEMENT_ICON_WIDTH = {
  lg: size.icon.md,
  md: size.icon.sm,
  sm: size.icon.xs
};
const HORIZONTAL_PADDING = {
  lg: Spacing.scale[2],
  md: Spacing.scale[1.5],
  sm: Spacing.scale[1.5]
};
const FLOATING_LABEL_PADDING = {
  lg: Spacing.scale[1.5],
  md: Spacing.scale[1],
  sm: Spacing.scale[1]
};
const GAP_SPACING = {
  lg: Spacing.scale[1],
  md: Spacing.scale[0.5],
  sm: Spacing.scale[0.5]
};
const LABEL_FONT_SIZE = {
  lg: text.typesets.body.md.fontSize,
  md: text.typesets.body.sm.fontSize,
  sm: text.typesets.body.xs.fontSize
};
const LABEL_TOP_POSITION = {
  lg: `-14px`,
  md: `-12px`,
  sm: `-10px`
};
const getLabelColor = (isFocused, hasError, isSuccessful, theme) => {
  if (hasError) {
    return isFocused ? theme === null || theme === void 0 ? void 0 : theme.colorScheme.status.default.border.negative : theme === null || theme === void 0 ? void 0 : theme.colorScheme.status.default.content.negative;
  }
  if (isSuccessful) {
    return isFocused ? theme === null || theme === void 0 ? void 0 : theme.colorScheme.status.default.border.positive : theme === null || theme === void 0 ? void 0 : theme.colorScheme.status.default.content.positive;
  }
  if (isFocused) {
    return theme === null || theme === void 0 ? void 0 : theme.colorScheme.input.border.focus;
  }
  return theme === null || theme === void 0 ? void 0 : theme.colorScheme.content.secondary;
};
const getLabelLeftPosition = (isFocused, hasValue, hasLeftElement, inputSize) => {
  if (isFocused || hasValue) {
    return FLOATING_LABEL_PADDING[inputSize];
  }
  if (hasLeftElement) {
    return `calc(${HORIZONTAL_PADDING[inputSize]} + ${FORM_ELEMENT_ICON_WIDTH[inputSize]} + ${GAP_SPACING[inputSize]})`;
  }
  return HORIZONTAL_PADDING[inputSize];
};
const createFormElementWrapper = () => _styled("div", {
  target: "e1ra8wu4"
})("position:relative;align-items:center;border-radius:", border.radius.md, ";border-style:solid;border-width:1px;box-shadow:none;display:flex;flex:1;width:100%;min-width:200px;font-size:", ({
  inputSize
}) => getFontSize(inputSize), ";height:", ({
  inputSize
}) => getSize(inputSize), ";outline:none;padding:", ({
  inputSize
}) => `0 ${HORIZONTAL_PADDING[inputSize]}`, ";transition:", FORM_ELEMENT_TRANSITION_VALUE, ";background-color:transparent;>[data-main-content]{flex:1;min-width:0;height:100%;display:flex;align-items:center;}border-color:", ({
  hasError,
  isReadOnly,
  isDisabled,
  isSuccessful,
  theme
}) => {
  if (isDisabled || isReadOnly) return theme.colorScheme.input.border.disabled;
  if (hasError) return theme.colorScheme.status.default.border.negative;
  if (isSuccessful) return theme.colorScheme.status.default.border.positive;
  return theme.colorScheme.input.border.enabled;
}, ";color:", ({
  isDisabled,
  theme
}) => isDisabled ? theme.colorScheme.content.disabled : theme.colorScheme.content.primary, ";:hover{border-color:", ({
  isDisabled,
  isReadOnly,
  isSuccessful,
  hasError,
  theme
}) => !isDisabled && !isReadOnly && !isSuccessful && !hasError ? theme.colorScheme.input.border.hover : undefined, ";}:focus-within{border-color:", ({
  hasError,
  isSuccessful,
  theme
}) => !hasError && !isSuccessful ? theme.colorScheme.input.border.focus : undefined, ";box-shadow:0 0 0 1px ", ({
  hasError,
  isSuccessful,
  theme
}) => hasError ? theme.colorScheme.status.default.border.negative : isSuccessful ? theme.colorScheme.status.default.border.positive : theme.colorScheme.input.border.focus, ";}");
const ElementWrapper = _styled("span", {
  target: "e1ra8wu3"
})({
  name: "zb6syf",
  styles: "align-items:center;display:flex;height:100%"
} );
const LeftElementWrapper = _styled(ElementWrapper, {
  target: "e1ra8wu2"
})("width:", ({
  inputSize = 'md'
}) => FORM_ELEMENT_ICON_WIDTH[inputSize], ";justify-content:center;margin-right:", ({
  inputSize = 'md'
}) => GAP_SPACING[inputSize], ";color:", ({
  theme
}) => theme.colorScheme.content.primary, ";");
const RightElementWrapper = _styled(ElementWrapper, {
  target: "e1ra8wu1"
})("width:", ({
  inputSize = 'md'
}) => FORM_ELEMENT_ICON_WIDTH[inputSize], ";justify-content:flex-end;margin-left:", ({
  inputSize = 'md'
}) => GAP_SPACING[inputSize], ";color:", ({
  theme
}) => theme.colorScheme.content.primary, ";");
const createFloatingLabel = () => _styled("label", {
  target: "e1ra8wu0"
})("position:absolute;pointer-events:none;transition:all 0.2s ease;font-size:", ({
  inputSize
}) => getFontSize(inputSize), ";left:", ({
  isFocused,
  hasValue,
  hasLeftElement = false,
  inputSize
}) => getLabelLeftPosition(isFocused, hasValue, hasLeftElement, inputSize), ";[data-left-element] &{left:", ({
  isFocused,
  hasValue,
  inputSize
}) => getLabelLeftPosition(isFocused, hasValue, true, inputSize), ";}", ({
  isFocused,
  hasValue,
  inputSize,
  theme,
  hasError,
  isSuccessful
}) => {
  if (isFocused || hasValue) {
    return css("top:", LABEL_TOP_POSITION[inputSize], ";transform:translateY(0);font-size:", LABEL_FONT_SIZE[inputSize], ";background-color:", theme.colorScheme.background.primary, ";padding:0 ", Spacing.scale[0.5], ";color:", getLabelColor(isFocused, hasError, isSuccessful, theme), ";");
  }
  return css("top:50%;transform:translateY(-50%);color:", theme.colorScheme.input.content.placeholder, ";");
}, " ", ({
  isDisabled,
  theme
}) => isDisabled && css("color:", theme.colorScheme.content.disabled, ";"), ";");

export { ElementWrapper, FLOATING_LABEL_PADDING, FORM_ELEMENT_ICON_WIDTH, GAP_SPACING, HORIZONTAL_PADDING, LABEL_FONT_SIZE, LABEL_TOP_POSITION, LeftElementWrapper, RightElementWrapper, createFloatingLabel, createFormElementWrapper, getLabelColor, getLabelLeftPosition };
